import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { HandUnderline, Burst, Swoosh, Leaf, OrganicSectionDivider } from "@/components/home/Doodles";
import { PHOTOS, Photo } from "@/components/home/photos";

const ease = [0.22, 1, 0.36, 1];

export function QuoteAccent({ children, className = "", lineClass = "w-40" }) {
  return (
    <p className={`font-hand text-ll-forest leading-[1.05] -rotate-6 ${className}`} data-testid="quote-accent">
      {children}
      <span className="block mt-1"><HandUnderline className={`${lineClass} h-3 text-ll-orange`} /></span>
    </p>
  );
}

export function CtaButton({ to, children, variant = "solid", testid, className = "" }) {
  const styles = {
    solid: "bg-ll-orange text-white hover:bg-ll-forest",
    outline: "border-2 border-ll-orange text-ll-orange hover:bg-ll-orange hover:text-white bg-white/70",
    forest: "bg-ll-forest text-white hover:bg-ll-orange",
    light: "bg-white text-ll-forest hover:bg-ll-orange hover:text-white",
  };
  return (
    <Link to={to} data-testid={testid} className={`group inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 min-h-[48px] text-[15px] font-semibold transition-colors duration-300 ${styles[variant]} ${className}`}>
      {children}
      <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1" />
    </Link>
  );
}

export default function HeroSection() {
  return (
    <section className="relative bg-ll-cream overflow-hidden" data-testid="home-hero">
      {/* Foto: mobiel boven, desktop rechts */}
      <div className="relative h-[340px] sm:h-[440px] lg:absolute lg:inset-y-0 lg:right-0 lg:left-[34%] lg:h-auto">
        <Photo photo={PHOTOS.hero} priority sizes="(min-width:1024px) 66vw, 100vw" className="absolute inset-0 w-full h-full object-[50%_30%]" />
        <div className="hidden lg:block absolute inset-y-0 left-0 w-[45%] bg-gradient-to-r from-ll-cream via-ll-cream/70 to-transparent" />
        <QuoteAccent className="absolute top-5 right-5 sm:top-10 sm:right-12 text-[26px] sm:text-4xl lg:text-[44px] max-w-[190px] sm:max-w-[300px] bg-ll-cream/75 lg:bg-transparent backdrop-blur-[2px] lg:backdrop-blur-0 rounded-2xl px-3 py-2 lg:p-0" lineClass="w-28 sm:w-40">
          Iedereen heeft talent. Soms zit er alleen iets tussen.
        </QuoteAccent>
        <div className="lg:hidden absolute -bottom-px inset-x-0"><OrganicSectionDivider /></div>
      </div>

      <div className="relative max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-12 pt-4 pb-20 lg:pt-24 lg:pb-40">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease }} className="relative lg:max-w-[600px]">
          <p className="flex items-center gap-2 text-[11px] sm:text-xs font-semibold uppercase tracking-[0.22em] text-ll-ink/80">
            Persoonlijke ontwikkeling met perspectief <Burst className="w-7 h-7 text-ll-orange" />
          </p>
          <h1 className="mt-5 font-heading font-semibold text-[40px] sm:text-6xl lg:text-[68px] leading-[1.0] tracking-[-0.02em] text-ll-forest" data-testid="home-hero-title">
            Sommige mensen hebben geen volgend traject nodig.{" "}
            <span className="text-ll-orange relative">
              Maar eerst een nieuw begin.
              <Swoosh className="hidden sm:inline-block w-16 h-6 ml-2 text-ll-sage align-middle" />
            </span>
          </h1>
          <p className="mt-6 text-lg sm:text-xl text-ll-ink/85 leading-relaxed max-w-[480px]">
            Twaalf weken persoonlijke ontwikkeling, inzicht en een passende volgende stap.
          </p>
          <div className="mt-9 flex flex-col sm:flex-row gap-3 sm:gap-4">
            <CtaButton to="/pilot" testid="home-hero-cta-pilot">Ontdek de gemeentelijke pilot</CtaButton>
            <CtaButton to="/voor-jou" variant="outline" testid="home-hero-cta-deelnemer">Ik ben deelnemer</CtaButton>
          </div>
        </motion.div>
      </div>
      <div className="hidden lg:block absolute -bottom-px inset-x-0 z-10">
        <OrganicSectionDivider />
        <Leaf className="absolute right-[6%] -top-16 w-28 text-ll-leaf" />
      </div>
    </section>
  );
}
